<script setup lang="ts">
import { useAuthStore } from '~/stores/auth.store';
import { useSiteStore } from '~/stores/site.store';
import { useServices } from '~/composables/useServices';
import { brandDomainSuffix, brandIds } from '~/assets/data/config';
import useLocaleUtils from '~/composables/useLocaleUtils';
import { useEnvironment } from '~/composables/useStates/useEnvironment';
import type { ISiteNotice } from '~/interfaces/stores/site.store.interface';

const auth = useAuthStore();
const { $walletService, $marketingService } = useServices();
const { $toggleChat, $t } = useNuxtApp();
const { getLocale } = useLocaleUtils();
const siteStore = useSiteStore();
const config = useRuntimeConfig();
const colorMode = useColorMode();
const environment = useEnvironment();

const bankingFrame = ref<HTMLIFrameElement>();

interface BankingProps {
  type?: string;
}
const props = withDefaults(defineProps<BankingProps>(), {
  type: '',
});

onMounted(async () => {
  window.onmessage = (event: MessageEvent) => {
    attachMessage(event);
  };
  const deviceInfo = await $marketingService.fetchDeviceInfo();
  window?.ProfileInit(deviceInfo.hash);
  await $marketingService.setSessionData();
});

onBeforeUnmount(() => {
  window.onmessage = null;
});

const url = computed(() => {
  const query = {
    msisdn: auth.currentUser.primaryMobileNumber,
    from: 'jpc',
    locale: getLocale(),
    theme: colorMode.preference,
  };
  const base = environment.value.environment.localhost
    ? `${config.public.bankingEnvironment}.${
        brandDomainSuffix[siteStore.getRegionCode]
      }`
    : document.location.hostname;
  const path = `/banking-app/${props.type}/`;

  const bankingUrl = new URL(path, `https://${base}`);
  Object.entries(query).forEach(([k, v]) => bankingUrl.searchParams.set(k, v));

  return bankingUrl.toString();
});

const tempData = computed(() => {
  return {
    UserData: {
      Method: 'setToken',
      Msisdn: auth.currentUser.username,
      BrandId: brandIds[siteStore.getRegionCode],
      Id: auth.userId,
      CommunicationChannel: 0,
      Token: auth.currentAccessToken,
    },
  };
});

const refreshUserBalances = async () => {
  await $walletService.fetchBalances();
};

const isIFrame = computed(
  () => !!bankingFrame.value && bankingFrame.value.tagName === 'IFRAME',
);

function attachMessage(event: MessageEvent) {
  const bankingType = props.type;
  if (event.source === bankingFrame.value.contentWindow) {
    if (event.data.toString().includes('refreshBalance')) {
      refreshUserBalances();
    }

    if (event.data.toString().includes('openChat')) {
      $toggleChat();
    }

    if (event.data.toString().includes('height')) {
      const resizeData = JSON.parse(event.data);
      bankingFrame.value.style.height = `${resizeData.height}px`;
    }

    if (event.data.toString().includes('loadedIframe')) {
      if (isIFrame && !!bankingFrame.value.contentWindow) {
        bankingFrame.value.contentWindow.postMessage(
          'loaded' + bankingType + 'Resize',
          '*',
        );
      }

      if (isIFrame && !!bankingFrame.value.contentWindow) {
        bankingFrame.value.contentWindow.postMessage(
          JSON.stringify(tempData.value),
          '*',
        );
      }
    }
    if (event.data.toString().includes('setAccountData')) {
      if (isIFrame && !!bankingFrame.value.contentWindow) {
        bankingFrame.value.contentWindow.postMessage(
          JSON.stringify(tempData.value),
          '*',
        );
      }
    }

    showDepositSpecific(event.data);
    showWithdrawalSpecific(event.data);
  }
}

function showDepositSpecific(data: Record<string, unknown>) {
  if (data.type === 'setdepositiframe') {
    if (isIFrame && !!bankingFrame.value.contentWindow)
      bankingFrame.value.setAttribute('scrolling', 'auto');
  }
  let message: string;
  if (data.type === 'refreshdepositbalance') {
    message = $t('toast.deposit-success.message');
    if (data.alert === 'warning')
      message = $t('toast.deposit-cancelled.message');
    else if (data.alert === 'error')
      message = $t('toast.deposit-failed.message');

    showToast({
      heading: $t('toast.deposit-status.heading'),
      content: { data: message },
      // @ts-expect-error - we don't know what banking might send
      severity: data.alert,
    });
    refreshUserBalances();
  }
  if (data.type === 'refreshdepositbalanceMM') {
    refreshUserBalances();
  }
}
function showWithdrawalSpecific(data: Record<string, unknown>) {
  if (data.type === 'withdrawrefreshbalance') {
    showToast({
      heading: $t('toast.successful-withdrawal.heading'),
      content: { data: $t('toast.successful-transaction.message') },
      severity: 'success',
    });
    refreshUserBalances();
  }
}

function showToast(obj: Partial<ISiteNotice>) {
  siteStore.toggleSiteNotice({
    ...obj,
    timeout: 3000,
    enabled: true,
  });
}
</script>
<template>
  <iframe
    :id="'banking_frame' + type"
    :key="type"
    ref="bankingFrame"
    :src="url"
    class="w-full max-view-width border-0 min-h-[550px] pt-1"
    allowtransparency="true"
  />
</template>
